/**
 * SessionContext
 * 
 * Unified context for session management: authentication + preferences + anonymous.
 * Single source of truth for all session-related data.
 * 
 * Uses the unified /session/init endpoint to get all data in ONE request.
 * 
 * Strategy:
 * 1. Load immediately from localStorage (no flash of loading)
 * 2. Fetch from /session/init in background to revalidate
 * 3. Update state and localStorage if data changed
 * 
 * Benefits:
 * - Single request instead of 2 sequential requests
 * - No queueing issues (auth doesn't block preferences)
 * - Centralized session management
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_CONFIG } from '@/core/config';
import i18n from '@/core/i18n/i18n';
import { api } from '@/core/api/apiClient';
import { authenticateSocket, disconnectSocket } from '@/core/realtime/socket';
import { setLastKnownUserId } from '@/core/identity/identity';
import { markJustLoggedIn } from '@/core/components/PostLoginChecker';

// ════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════

export interface StoreInfo {
  id: string;
  slug: string;
  name: string;
  countryCode: string | null;
  currencyCode: string;
  localeCode: string;
  isDefault?: boolean;
  freeShippingThreshold?: number | null;
}

export interface SessionUser {
  id: string;
  email: string | null;
  walletAddress?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureUrl?: string | null;
  accountVerified?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  twoFactorEnabled?: boolean;
}

interface SessionPreferences {
  languageCode: string | null;
  storeId: string | null;
  storeSlug: string | null;
  countryCode: string | null;
  theme: 'light' | 'dark' | 'auto' | null;
}

interface SessionConsent {
  termsAccepted: boolean;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
  marketingConsent: boolean;
  marketingConsentAt: string | null;
  requiresTermsAcceptance: boolean;
}

interface SessionInitResponse {
  stores: StoreInfo[];
  preferences: SessionPreferences;
  isAuthenticated: boolean;
  user: SessionUser | null;
  consent: SessionConsent | null;
  isNewVisitor: boolean;
  clientId: string | null;
}

interface SessionContextValue {
  // ══════════════════════════════════════════════════════════════════════════
  // AUTH
  // ══════════════════════════════════════════════════════════════════════════
  user: SessionUser | null;
  isAuthenticated: boolean;
  login: (accessToken: string, refreshToken: string, userData?: SessionUser | null) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  
  // ══════════════════════════════════════════════════════════════════════════
  // PREFERENCES
  // ══════════════════════════════════════════════════════════════════════════
  
  // Language
  language: string;
  setLanguage: (code: string) => Promise<void>;
  
  // Store
  currentStore: StoreInfo | null;
  availableStores: StoreInfo[];
  setStore: (storeId: string) => Promise<void>;
  
  // Theme
  theme: 'light' | 'dark' | 'auto';
  setTheme: (theme: 'light' | 'dark' | 'auto') => Promise<void>;
  
  // Derived helpers
  storeSlug: string | null;
  storeCurrency: string | null;
  detectedCountry: string | null;
  
  // ══════════════════════════════════════════════════════════════════════════
  // CONSENT & METADATA
  // ══════════════════════════════════════════════════════════════════════════
  consent: SessionConsent | null;
  clientId: string | null;
  isNewVisitor: boolean;
  
  // ══════════════════════════════════════════════════════════════════════════
  // LOADING & UTILITIES
  // ══════════════════════════════════════════════════════════════════════════
  isLoading: boolean;
  refetch: () => void;
}

// ════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════════════════════

const STORAGE_KEYS = {
  // Auth
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  // Preferences
  LANGUAGE: 'user_language_preference',
  STORE_ID: 'user_store_preference',
  STORE_SLUG: 'last_store_slug',
  DETECTED_STORE: 'detected_store_id',
  THEME: 'user_theme_preference',
  STORES_CACHE: 'session_stores_cache',
} as const;

// ════════════════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════════════════

function loadCachedStores(): StoreInfo[] {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.STORES_CACHE);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // Invalid cache, ignore
  }
  return [];
}

function saveCachedStores(stores: StoreInfo[]): void {
  try {
    if (stores.length > 0) {
      localStorage.setItem(STORAGE_KEYS.STORES_CACHE, JSON.stringify(stores));
    }
  } catch {
    // localStorage full or unavailable, ignore
  }
}

function hasValidToken(): boolean {
  return !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

// ════════════════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

async function fetchSessionInit(): Promise<SessionInitResponse> {
  const response = await api.get<SessionInitResponse>('/session/init');
  return (response.data as any).data || response.data;
}

async function updateSessionPreferences(data: {
  languageCode?: string | null;
  storeId?: string | null;
  theme?: 'light' | 'dark' | 'auto' | null;
}): Promise<void> {
  await api.patch('/session/preferences', data);
}

// ════════════════════════════════════════════════════════════════════════════
// CONTEXT
// ════════════════════════════════════════════════════════════════════════════

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  
  // ══════════════════════════════════════════════════════════════════════════
  // AUTH STATE (from localStorage initially, then from /session/init)
  // ══════════════════════════════════════════════════════════════════════════
  
  const [user, setUser] = useState<SessionUser | null>(null);
  // We start loading only if there's a token to validate
  const [authChecked, setAuthChecked] = useState(!hasValidToken());
  
  // ══════════════════════════════════════════════════════════════════════════
  // PREFERENCES STATE (from localStorage initially, no flash)
  // ══════════════════════════════════════════════════════════════════════════
  
  const [language, setLanguageState] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.LANGUAGE) || i18n.language || 'es';
  });
  
  const [currentStoreId, setCurrentStoreId] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.STORE_ID) 
      || localStorage.getItem(STORAGE_KEYS.DETECTED_STORE) 
      || null;
  });
  
  const [theme, setThemeState] = useState<'light' | 'dark' | 'auto'>(() => {
    return (localStorage.getItem(STORAGE_KEYS.THEME) as 'light' | 'dark' | 'auto') || 'auto';
  });
  
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  
  // Cached stores from localStorage (for instant dropdown population)
  const [cachedStores] = useState<StoreInfo[]>(() => loadCachedStores());
  
  // ══════════════════════════════════════════════════════════════════════════
  // FETCH SESSION DATA (single request for everything)
  // ══════════════════════════════════════════════════════════════════════════
  
  const { data: sessionData, isLoading: sessionLoading, refetch, isError } = useQuery({
    queryKey: ['session', 'init'],
    queryFn: fetchSessionInit,
    ...QUERY_CONFIG.STABLE,
    // Always fetch immediately - no waiting for auth
    enabled: true,
    refetchOnMount: true,
  });
  
  // Parse stores from session data, fallback to cached stores for instant load
  const availableStores = useMemo((): StoreInfo[] => {
    return sessionData?.stores || cachedStores;
  }, [sessionData, cachedStores]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // SYNC AUTH STATE FROM SESSION DATA
  // ══════════════════════════════════════════════════════════════════════════
  
  // Guard to prevent infinite refresh loops: only attempt once per mount
  const tokenRefreshAttemptedRef = useRef(false);
  
  useEffect(() => {
    if (!sessionData) return;
    
    if (sessionData.isAuthenticated && sessionData.user) {
      setUser(sessionData.user);
      tokenRefreshAttemptedRef.current = false;
      
      if (sessionData.user.id) {
        setLastKnownUserId(sessionData.user.id);
      }
      
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (token) {
        authenticateSocket(token)
          .then(() => console.log('[Session] Socket authenticated'))
          .catch((err) => console.warn('[Session] Socket auth failed:', err.message));
      }
    } else {
      // Backend says not authenticated.
      // /session/init uses addUserIdMiddleware which silently ignores expired tokens
      // (returns 200, not 401), so the axios interceptor never refreshes.
      // If we have tokens in localStorage, try to refresh before giving up.
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      if (accessToken && refreshToken && !tokenRefreshAttemptedRef.current) {
        tokenRefreshAttemptedRef.current = true;
        console.log('[Session] Token likely expired, attempting proactive refresh...');
        
        api.post('/auth/refresh', { refreshToken })
          .then((response) => {
            const newToken = (response.data as any).data?.accessToken;
            if (newToken) {
              localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newToken);
              console.log('[Session] Token refreshed, re-fetching session');
              queryClient.invalidateQueries({ queryKey: ['session', 'init'] });
            } else {
              setUser(null);
              setAuthChecked(true);
            }
          })
          .catch(() => {
            console.log('[Session] Refresh failed, clearing session');
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            setUser(null);
            setAuthChecked(true);
          });
        return;
      }
      
      setUser(null);
    }
    
    setAuthChecked(true);
  }, [sessionData, queryClient]);
  
  // Handle session fetch error (e.g., token expired)
  useEffect(() => {
    if (isError && hasValidToken()) {
      // Session fetch failed but we have a token - might be expired
      // Don't clear tokens on network errors, only on auth errors
      console.warn('[Session] Failed to fetch session, token might be invalid');
    }
    
    if (isError || sessionData) {
      setAuthChecked(true);
    }
  }, [isError, sessionData]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // SYNC PREFERENCES STATE FROM SESSION DATA
  // ══════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (!sessionData) return;
    
    const prefs = sessionData.preferences;
    
    // Sync language if different
    if (prefs.languageCode && prefs.languageCode !== language) {
      setLanguageState(prefs.languageCode);
      i18n.changeLanguage(prefs.languageCode);
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, prefs.languageCode);
    }
    
    // Sync store if different
    if (prefs.storeId && prefs.storeId !== currentStoreId) {
      setCurrentStoreId(prefs.storeId);
      localStorage.setItem(STORAGE_KEYS.STORE_ID, prefs.storeId);
      
      const store = sessionData.stores.find(s => s.id === prefs.storeId);
      if (store) {
        localStorage.setItem(STORAGE_KEYS.STORE_SLUG, store.slug);
      }
    }
    
    // Sync theme if different
    if (prefs.theme && prefs.theme !== theme) {
      setThemeState(prefs.theme);
      localStorage.setItem(STORAGE_KEYS.THEME, prefs.theme);
    }
    
    // Store detected country
    if (prefs.countryCode) {
      setDetectedCountry(prefs.countryCode);
    }
    
    // Cache stores for instant load on next visit
    if (sessionData.stores && sessionData.stores.length > 0) {
      saveCachedStores(sessionData.stores);
    }
  }, [sessionData]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // SYNC LOCALSTORAGE → BACKEND IF BACKEND HAS NULLS
  // ══════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (!sessionData || !sessionData.isAuthenticated) return;
    
    const prefs = sessionData.preferences;
    const cachedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    const cachedStoreId = localStorage.getItem(STORAGE_KEYS.STORE_ID);
    const cachedTheme = localStorage.getItem(STORAGE_KEYS.THEME);
    
    const needsSync = (
      (prefs.storeId === null && cachedStoreId) ||
      (prefs.languageCode === null && cachedLanguage) ||
      (prefs.theme === null && cachedTheme)
    );
    
    if (needsSync) {
      console.log('[Session] Syncing localStorage preferences to backend');
      
      const updatePayload: {
        languageCode?: string | null;
        storeId?: string | null;
        theme?: 'light' | 'dark' | 'auto' | null;
      } = {};
      
      if (prefs.languageCode === null && cachedLanguage) {
        updatePayload.languageCode = cachedLanguage;
      }
      if (prefs.storeId === null && cachedStoreId) {
        updatePayload.storeId = cachedStoreId;
      }
      if (prefs.theme === null && cachedTheme) {
        updatePayload.theme = cachedTheme as 'light' | 'dark' | 'auto';
      }
      
      updateSessionPreferences(updatePayload)
        .then(() => {
          console.log('[Session] Successfully synced localStorage to backend');
          queryClient.invalidateQueries({ queryKey: ['session', 'init'] });
        })
        .catch((error) => {
          console.warn('[Session] Failed to sync localStorage to backend:', error);
        });
    }
  }, [sessionData, queryClient]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // AUTO-SELECT DEFAULT STORE
  // ══════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (!currentStoreId && availableStores.length > 0 && !sessionLoading) {
      const defaultStore = availableStores.find(s => s.isDefault) || availableStores[0];
      if (defaultStore) {
        setCurrentStoreId(defaultStore.id);
        localStorage.setItem(STORAGE_KEYS.STORE_ID, defaultStore.id);
        localStorage.setItem(STORAGE_KEYS.STORE_SLUG, defaultStore.slug);
        console.log('[Session] Auto-selected default store:', defaultStore.slug);
      }
    }
  }, [currentStoreId, availableStores, sessionLoading]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // DERIVED VALUES
  // ══════════════════════════════════════════════════════════════════════════
  
  const currentStore = useMemo((): StoreInfo | null => {
    if (!currentStoreId || availableStores.length === 0) return null;
    return availableStores.find(s => s.id === currentStoreId) || null;
  }, [currentStoreId, availableStores]);
  
  const storeSlug = currentStore?.slug || null;
  const storeCurrency = currentStore?.currencyCode || null;
  const isAuthenticated = !!user;
  
  // ══════════════════════════════════════════════════════════════════════════
  // AUTH ACTIONS
  // ══════════════════════════════════════════════════════════════════════════
  
  const login = useCallback((accessToken: string, refreshToken: string, userData?: SessionUser | null) => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    
    // Authenticate socket
    authenticateSocket(accessToken)
      .then(() => console.log('[Session] Socket authenticated successfully'))
      .catch((err) => console.error('[Session] Socket authentication failed:', err.message));
    
    // Mark for passkey promotion check
    markJustLoggedIn();
    
    // If we have user data from login response, use it immediately
    if (userData) {
      setUser(userData);
      if (userData.id) {
        setLastKnownUserId(userData.id);
      }
    }
    
    // Refetch session to get fresh data (preferences, consent, etc.)
    queryClient.invalidateQueries({ queryKey: ['session', 'init'] });
  }, [queryClient]);
  
  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    setUser(null);
    disconnectSocket();
    
    // Refetch session as anonymous
    queryClient.invalidateQueries({ queryKey: ['session', 'init'] });
  }, [queryClient]);
  
  const refreshUser = useCallback(async () => {
    await refetch();
  }, [refetch]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // PREFERENCE ACTIONS
  // ══════════════════════════════════════════════════════════════════════════
  
  const setLanguage = useCallback(async (code: string) => {
    setLanguageState(code);
    i18n.changeLanguage(code);
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, code);
    
    try {
      await updateSessionPreferences({ languageCode: code });
      queryClient.invalidateQueries({ queryKey: ['session', 'init'] });
    } catch (error) {
      console.warn('[Session] Failed to save language:', error);
    }
  }, [queryClient]);
  
  const setStore = useCallback(async (storeId: string) => {
    const store = availableStores.find(s => s.id === storeId);
    if (!store) {
      console.warn('[Session] Store not found:', storeId);
      return;
    }
    
    setCurrentStoreId(storeId);
    localStorage.setItem(STORAGE_KEYS.STORE_ID, storeId);
    localStorage.setItem(STORAGE_KEYS.STORE_SLUG, store.slug);
    
    console.log('[Session] Store changed to:', store.slug);
    
    try {
      await updateSessionPreferences({ storeId });
      queryClient.invalidateQueries({ queryKey: ['session', 'init'] });
    } catch (error) {
      console.warn('[Session] Failed to save store:', error);
    }
  }, [availableStores, queryClient]);
  
  const setTheme = useCallback(async (newTheme: 'light' | 'dark' | 'auto') => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEYS.THEME, newTheme);
    
    if (isAuthenticated) {
      try {
        await updateSessionPreferences({ theme: newTheme });
        queryClient.invalidateQueries({ queryKey: ['session', 'init'] });
      } catch (error) {
        console.warn('[Session] Failed to save theme:', error);
      }
    }
  }, [isAuthenticated, queryClient]);
  
  // ══════════════════════════════════════════════════════════════════════════
  // CONTEXT VALUE
  // ══════════════════════════════════════════════════════════════════════════
  
  const isLoading = !authChecked || sessionLoading;
  const consent = sessionData?.consent || null;
  const clientId = sessionData?.clientId || null;
  const isNewVisitor = sessionData?.isNewVisitor || false;

  const value = useMemo<SessionContextValue>(() => ({
    // Auth
    user,
    isAuthenticated,
    login,
    logout,
    refreshUser,
    
    // Language
    language,
    setLanguage,
    
    // Store
    currentStore,
    availableStores,
    setStore,
    
    // Theme
    theme,
    setTheme,
    
    // Derived
    storeSlug,
    storeCurrency,
    detectedCountry,
    
    // Consent & Metadata
    consent,
    clientId,
    isNewVisitor,
    
    // Loading
    isLoading,
    
    // Refetch
    refetch,
  }), [
    user,
    isAuthenticated,
    login,
    logout,
    refreshUser,
    language,
    setLanguage,
    currentStore,
    availableStores,
    setStore,
    theme,
    setTheme,
    storeSlug,
    storeCurrency,
    detectedCountry,
    consent,
    clientId,
    isNewVisitor,
    isLoading,
    refetch,
  ]);
  
  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// HOOKS
// ════════════════════════════════════════════════════════════════════════════

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

/**
 * Convenience hook for accessing just preferences
 * (backwards compatible with usePreferences)
 */
export function useSessionPreferences() {
  const session = useSession();
  return {
    language: session.language,
    setLanguage: session.setLanguage,
    currentStore: session.currentStore,
    availableStores: session.availableStores,
    setStore: session.setStore,
    theme: session.theme,
    setTheme: session.setTheme,
    storeSlug: session.storeSlug,
    storeCurrency: session.storeCurrency,
    detectedCountry: session.detectedCountry,
    consent: session.consent,
    isLoading: session.isLoading,
    refetch: session.refetch,
  };
}

/**
 * Convenience hook for accessing just auth
 * (backwards compatible with useAuth)
 */
export function useSessionAuth() {
  const session = useSession();
  return {
    user: session.user,
    isAuthenticated: session.isAuthenticated,
    isLoading: session.isLoading,
    login: session.login,
    logout: session.logout,
    refreshUser: session.refreshUser,
  };
}
